import React, { useState } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'

import type { CancelDealReqBody, DealEntity } from '../../../apis/dealsAPI'
import { useCancelDealMutation } from '../hooks/useCancelDealMutation'
import { useDealQuery } from '../hooks/useDealQuery'
import { useModal } from '../state/ModalProvider'
import { formatActivationLabel } from '../utils/dealUrgency'

interface CancelSubscriptionModalProps {
  dealId: string
}

type CancelOption = CancelDealReqBody['mode']

interface OptionDescriptor {
  key: CancelOption
  title: string
  desc: string
}

const CANCEL_OPTIONS: OptionDescriptor[] = [
  {
    key: 'end_of_period',
    title: 'Cancel at end of current period',
    desc: 'No more invoices generated. Existing scheduled invoices stay live until billed.',
  },
  {
    key: 'immediate',
    title: 'Cancel immediately',
    desc: 'Stop billing now. Pro-rata refund may apply for unused service.',
  },
]

export const CancelSubscriptionModal: React.FunctionComponent<CancelSubscriptionModalProps> = (
  props,
) => {
  const { dealId } = props
  const { close } = useModal()
  const dealQuery = useDealQuery(dealId)
  const cancelMutation = useCancelDealMutation()

  const [choice, setChoice] = useState<CancelOption>('end_of_period')

  const deal: DealEntity | null = dealQuery.data ?? null

  function stop(event: ReactMouseEvent): void {
    event.stopPropagation()
  }

  function handleConfirm(): void {
    if (!deal) return
    cancelMutation.mutate(
      { dealId: deal.id, body: { mode: choice } },
      {
        // Closes both this CancelSubscriptionModal and the EditDealModal it
        // was launched from — useModal only tracks one modal at a time, so a
        // single close() collapses the whole stack back to the deals list.
        onSuccess: () => close(),
      },
    )
  }

  if (!deal) {
    // Deal query is still warming up (modal opened straight after row click);
    // render an empty overlay so the click-outside handler stays in place.
    return <div className="cancel-dialog-overlay active" onClick={close} />
  }

  return (
    <div className="cancel-dialog-overlay active" onClick={close}>
      <div className="cancel-dialog" onClick={stop}>
        <div className="cancel-dialog-icon" aria-hidden>
          ⚠
        </div>
        <h3>Cancel Subscription?</h3>
        <p className="subtitle">This action cannot be undone easily.</p>

        <div className="cancel-info-card">
          <div className="cancel-info-row">
            <span className="label">Deal</span>
            <span className="value">{deal.orderNumber}</span>
          </div>
          <div className="cancel-info-row">
            <span className="label">Billing</span>
            <span className="value">{deal.billingCadence}</span>
          </div>
          <div className="cancel-info-row">
            <span className="label">Active</span>
            <span className="value">{formatActivationLabel(deal.activationDate) || '—'}</span>
          </div>
          <div className="cancel-info-row">
            <span className="label">Expires</span>
            <span className="value">{formatActivationLabel(deal.expirationDate) || '—'}</span>
          </div>
        </div>

        {CANCEL_OPTIONS.map((option) => (
          <div
            key={option.key}
            className={`cancel-radio-option${choice === option.key ? ' selected' : ''}`}
            onClick={() => setChoice(option.key)}
          >
            <label>
              <input
                type="radio"
                name="cancelType"
                checked={choice === option.key}
                onChange={() => setChoice(option.key)}
              />
              <div className="cancel-radio-text">
                <div className="title">{option.title}</div>
                <div className="desc">{option.desc}</div>
              </div>
            </label>
          </div>
        ))}

        <div className="cancel-warning">
          <span className="icon" aria-hidden>
            ⚠
          </span>
          <span>
            Active shipments stay deliverable. Re-activation requires a new deal entry.
          </span>
        </div>

        <div className="cancel-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={close}
            disabled={cancelMutation.isLoading}
          >
            Keep Subscription
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={handleConfirm}
            disabled={cancelMutation.isLoading}
          >
            {cancelMutation.isLoading ? 'Canceling…' : 'Confirm Cancellation'}
          </button>
        </div>
      </div>
    </div>
  )
}
